import { useState } from 'react';
import { ChevronLeft, ChevronRight, Eye, RotateCw, ShieldCheck } from 'lucide-react';
import type { AuditRow, CursorPage } from '@dru/types';
import { Button, DataTable, FilterBar, IconAction, InfoTip, RowActions, StatusBadge, type Tone } from '@dru/ui';
import { api } from '../../../lib/api';
import { formatDateTime } from '../../../lib/format';
import { useQuery } from '../../../lib/useQuery';
import { Dialog } from '../../../components/Dialog';

/* ── Activity ───────────────────────────────────────────────── */

function words(key: string): string {
  const spaced = key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const ACTION_MAP: Record<string, { label: string; category: string }> = {
  'crosstenant.plant.read.requested': { label: 'Requested plants', category: 'Record access' },
  'crosstenant.employee.read.requested': { label: 'Requested workforce', category: 'Record access' },
  'crosstenant.examination.read.requested': { label: 'Requested examinations', category: 'Record access' },
  'crosstenant.certificate.read.requested': { label: 'Requested certificates', category: 'Record access' },
  // Privileged access
  'crosstenant.certificate.read': { label: 'Read certificates', category: 'Record access' },
  'crosstenant.examination.read': { label: 'Read examinations', category: 'Record access' },
  'crosstenant.employee.read': { label: 'Read the workforce', category: 'Record access' },
  'crosstenant.plant.read': { label: 'Read plants', category: 'Record access' },
  'crosstenant.audit.read': { label: 'Read the audit trail', category: 'Record access' },
  'crosstenant.tenant.read': { label: 'Read company details', category: 'Record access' },

  // Lifecycle
  'tenant.register': { label: 'Company registered', category: 'Account' },
  'tenant.register.email-failed': { label: 'Registration email failed', category: 'Account' },
  'tenant.approve': { label: 'Approved and activated', category: 'Account' },
  'tenant.reject': { label: 'Registration rejected', category: 'Account' },
  'tenant.update': { label: 'Company details changed', category: 'Account' },
  'tenant.welcome-resend': { label: 'Welcome email sent again', category: 'Account' },
  'tenant.suspend': { label: 'Company suspended', category: 'Account' },
  'tenant.reinstate': { label: 'Company reinstated', category: 'Account' },

  // Onboarding
  'onboarding.step': { label: 'Setup step marked', category: 'Setup' },
  'onboarding.update': { label: 'Setup details changed', category: 'Setup' },
  'onboarding.complete': { label: 'Setup completed', category: 'Setup' },
  'onboarding.status': { label: 'Setup status changed', category: 'Setup' },
  'platform.setup': { label: 'Setup by the platform', category: 'Setup' },

  // Settings, access, billing
  'settings.health-report-access': { label: 'Health report access changed', category: 'Settings' },
  'user.create': { label: 'User account created', category: 'Access' },
  'platform.provision': { label: 'Provisioned by the platform', category: 'System' },
  'platform.billing': { label: 'Billing read by the platform', category: 'Billing' },
  'invoice.create': { label: 'Invoice generated', category: 'Billing' },
  'invoice.approve': { label: 'Invoice issued', category: 'Billing' },
  'ratecard.update': { label: 'Rate card changed', category: 'Billing' },
};

function formatAction(action: string): { label: string; category: string } {
  if (ACTION_MAP[action]) return ACTION_MAP[action];
  const parts = action.split('.');
  if (parts.length >= 2) {
    return { category: words(parts[0]!), label: parts.slice(1).map(words).join(' ') };
  }
  return { label: words(action), category: 'Activity' };
}

/**
 * A category is a kind of event, not a state, so it is neutral — a billing
 * event drawn in the warning colour reads as a billing problem. Privileged
 * access is the one kind a reader scans for, and takes the info tone.
 */
function categoryTone(category: string): Tone {
  return category === 'Record access' ? 'info' : 'neutral';
}

function outcomeTone(outcome: string): Tone {
  return outcome === 'SUCCESS' ? 'covered' : outcome === 'DENIED' ? 'warning' : 'danger';
}

function metaOf(r: AuditRow): Record<string, unknown> | null {
  return r.metadata && typeof r.metadata === 'object' ? (r.metadata as Record<string, unknown>) : null;
}

function renderDetailsSummary(r: AuditRow) {
  const meta = metaOf(r);
  const reason = meta?.reason ? String(meta.reason) : null;
  const ticketRef = meta?.ticketRef ? String(meta.ticketRef) : null;
  const what = meta?.what ? String(meta.what) : null;
  const changedFields = Array.isArray(meta?.changedFields) ? (meta.changedFields as string[]) : null;
  const recipients = Array.isArray(meta?.recipients) ? (meta.recipients as string[]) : null;
  const address = meta?.address ? String(meta.address) : null;
  const nothing = !reason && !ticketRef && !what && !changedFields && !recipients && !address;

  return (
    <div className="space-y-0.5 text-caption font-normal">
      {what && <div className="text-neutral-700">{what.charAt(0).toUpperCase() + what.slice(1)}</div>}
      {reason && (
        <div className="text-neutral-800">
          <span className="font-medium text-neutral-500">Reason: </span>
          <span className="line-clamp-2">&ldquo;{reason}&rdquo;</span>
        </div>
      )}
      {ticketRef && <div className="font-mono text-neutral-700">Ticket {ticketRef}</div>}
      {changedFields && changedFields.length > 0 && (
        <div className="text-neutral-600">
          <span className="font-medium text-neutral-500">Changed: </span>
          {changedFields.slice(0, 3).join(', ')}{changedFields.length > 3 ? ` and ${changedFields.length - 3} more` : ''}
        </div>
      )}
      {recipients && recipients.length > 0 && (
        <div className="max-w-xs truncate text-neutral-600">
          <span className="font-medium text-neutral-500">Sent to: </span>{recipients.join(', ')}
        </div>
      )}
      {address && (
        <div className="max-w-xs truncate text-neutral-600">
          <span className="font-medium text-neutral-500">To: </span>{address}
        </div>
      )}
      {nothing && <span className="text-neutral-500">No further detail</span>}
    </div>
  );
}

function ActivityDetailDialog({ row, onClose }: { row: AuditRow; onClose: () => void }) {
  const actionMeta = formatAction(row.action);
  const meta = metaOf(row);
  const reason = meta?.reason ? String(meta.reason) : null;
  const ticketRef = meta?.ticketRef ? String(meta.ticketRef) : null;
  const block = 'max-h-52 overflow-auto rounded bg-neutral-50 p-2 font-mono text-caption font-normal text-neutral-800';

  return (
    <Dialog title={actionMeta.label} onClose={onClose} wide>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge tone={categoryTone(actionMeta.category)}>{actionMeta.category}</StatusBadge>
          <StatusBadge tone={outcomeTone(row.outcome)}>{row.outcome === 'SUCCESS' ? 'Succeeded' : words(row.outcome)}</StatusBadge>
          <span className="font-mono text-caption text-neutral-500">{row.action}</span>
        </div>

        {Boolean(reason || ticketRef) && (
          <div className="rounded-card border border-info-600/30 bg-info-100 p-3">
            <div className="flex items-start gap-2.5">
              <ShieldCheck aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-info-600" />
              <div className="space-y-1">
                <div className="text-caption font-semibold text-info-600">Reason given</div>
                {reason && <p className="text-small font-medium text-neutral-900">&ldquo;{reason}&rdquo;</p>}
                {ticketRef && <p className="font-mono text-caption text-neutral-700">Ticket {ticketRef}</p>}
              </div>
            </div>
          </div>
        )}

        <dl className="grid grid-cols-2 gap-3 rounded-card border border-neutral-200 p-3 sm:grid-cols-4">
          <div>
            <dt className="text-caption text-neutral-500">When</dt>
            <dd className="text-small font-medium text-neutral-900">{formatDateTime(row.occurredAt)}</dd>
          </div>
          <div>
            <dt className="text-caption text-neutral-500">By</dt>
            <dd className="text-small font-medium text-neutral-900">{row.actorRole ? words(row.actorRole) : 'The system'}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-caption text-neutral-500">Account ID</dt>
            <dd className="truncate font-mono text-caption text-neutral-800" title={row.actorId ?? undefined}>{row.actorId ?? 'None'}</dd>
          </div>
          <div>
            <dt className="text-caption text-neutral-500">IP address</dt>
            <dd className="font-mono text-caption text-neutral-800">{row.ip ?? 'Not recorded'}</dd>
          </div>
        </dl>

        {Boolean(row.before || row.after) && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-card border border-neutral-200 p-3">
              <h3 className="mb-2 text-caption font-semibold uppercase tracking-wider text-neutral-500">Before</h3>
              <pre className={block}>{row.before ? JSON.stringify(row.before, null, 2) : 'Nothing'}</pre>
            </div>
            <div className="rounded-card border border-neutral-200 p-3">
              <h3 className="mb-2 text-caption font-semibold uppercase tracking-wider text-neutral-500">After</h3>
              <pre className={block}>{row.after ? JSON.stringify(row.after, null, 2) : 'Nothing'}</pre>
            </div>
          </div>
        )}

        {Boolean(meta && Object.keys(meta).length > 0) && (
          <div className="rounded-card border border-neutral-200 p-3">
            <h3 className="mb-2 text-caption font-semibold uppercase tracking-wider text-neutral-500">Recorded detail</h3>
            <pre className={block}>{JSON.stringify(row.metadata, null, 2)}</pre>
          </div>
        )}
      </div>
      <div className="mt-5 flex justify-end">
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </div>
    </Dialog>
  );
}

const CATEGORIES = [
  { value: 'crosstenant', label: 'Privileged access' },
  { value: 'tenant', label: 'Account' },
  { value: 'onboarding', label: 'Setup' },
  { value: 'settings', label: 'Settings' },
];

/**
 * Everything recorded against this company, newest first: account changes,
 * setup, billing reads, and every privileged read with the reason it was
 * made under. A row opens the full record.
 */
export function CompanyActivity({ tenantId }: { tenantId: string }) {
  const [category, setCategory] = useState<string>('');
  const [pageSize, setPageSize] = useState<number>(25);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [cursorHistory, setCursorHistory] = useState<(string | null)[]>([null]);
  const [inspectRow, setInspectRow] = useState<AuditRow | null>(null);

  const currentCursor = cursorHistory[pageIndex] ?? null;
  const url = `/audit?entityType=Tenant&entityId=${tenantId}&limit=${pageSize}${category ? `&action=${encodeURIComponent(category)}` : ''}${currentCursor ? `&cursor=${encodeURIComponent(currentCursor)}` : ''}`;
  const trail = useQuery<CursorPage<AuditRow>>(() => api.get(url), [url]);

  const restart = () => { setPageIndex(0); setCursorHistory([null]); };
  function handleNextPage() {
    if (trail.data?.nextCursor) {
      setCursorHistory((prev) => [...prev.slice(0, pageIndex + 1), trail.data!.nextCursor]);
      setPageIndex((p) => p + 1);
    }
  }

  const rows = trail.data?.items ?? [];

  return (
    <div className="space-y-3">
      <h2 className="flex items-center gap-1 text-body font-semibold text-neutral-900">
        Activity
        <InfoTip label="About the activity">
          Everything recorded against this company — account changes, setup, billing, and each privileged read with its reason. It cannot be edited.
        </InfoTip>
      </h2>

      <FilterBar
        filters={[{ key: 'category', label: 'Kind of activity', options: CATEGORIES }]}
        value={{ category: category || undefined }}
        onChange={(v) => { setCategory(v.category ?? ''); restart(); }}
        actions={(
          <Button
            variant="secondary"
            size="sm"
            onClick={trail.reload}
            loading={trail.loading}
            icon={<RotateCw aria-hidden className="h-3.5 w-3.5" />}
          >
            Refresh
          </Button>
        )}
      />

      <DataTable<AuditRow>
        rows={rows}
        loading={trail.loading}
        error={trail.error}
        onRetry={trail.reload}
        rowKey={(r) => r.id}
        onRowClick={(r) => setInspectRow(r)}
        empty={{
          title: category ? 'Nothing of this kind yet' : 'No activity recorded',
          hint: category ? 'Clear the filter to see everything.' : 'Registration, approval, changes and privileged reads appear here as they happen.',
        }}
        columns={[
          {
            key: 'action', header: 'Event', alwaysVisible: true,
            cell: (r) => {
              const m = formatAction(r.action);
              return (
                <div className="min-w-0">
                  <div className="text-small font-medium text-neutral-900">{m.label}</div>
                  <div className="mt-0.5"><StatusBadge tone={categoryTone(m.category)}>{m.category}</StatusBadge></div>
                </div>
              );
            },
          },
          {
            key: 'when', header: 'When',
            cell: (r) => <span className="whitespace-nowrap text-small tabular-nums text-neutral-600">{formatDateTime(r.occurredAt)}</span>,
          },
          {
            key: 'actor', header: 'By',
            cell: (r) => <span className="text-small text-neutral-700">{r.actorRole ? words(r.actorRole) : 'The system'}</span>,
          },
          { key: 'details', header: 'Detail', hideOnMobile: true, cell: (r) => renderDetailsSummary(r) },
          {
            key: 'outcome', header: 'Outcome', mobileLabel: false,
            cell: (r) => (
              <StatusBadge tone={outcomeTone(r.outcome)}>
                {r.outcome === 'SUCCESS' ? 'Succeeded' : words(r.outcome)}
              </StatusBadge>
            ),
          },
          {
            key: 'go', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '',
            cell: (r) => (
              <RowActions>
                <IconAction label={`View ${formatAction(r.action).label}`} icon={Eye} onClick={() => setInspectRow(r)} />
              </RowActions>
            ),
          },
        ]}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-caption text-neutral-600">
          <label htmlFor={`activity-size-${tenantId}`}>Rows per page</label>
          <select
            id={`activity-size-${tenantId}`}
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); restart(); }}
            className="h-8 max-md:h-10 rounded-input border border-neutral-300 bg-white px-2 text-caption text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <span aria-hidden className="text-neutral-400">·</span>
          <span>Page {pageIndex + 1}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary" size="sm"
            disabled={pageIndex === 0 || trail.loading}
            onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
            icon={<ChevronLeft aria-hidden className="h-3.5 w-3.5" />}
          >
            Newer
          </Button>
          <Button
            variant="secondary" size="sm"
            disabled={!trail.data?.nextCursor || trail.loading}
            onClick={handleNextPage}
          >
            Older <ChevronRight aria-hidden className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {inspectRow && <ActivityDetailDialog row={inspectRow} onClose={() => setInspectRow(null)} />}
    </div>
  );
}
